import axiosClient from './axiosClient';

export interface DocumentDto {
  id: string;
  opportunityId: string;
  fileName: string;
  fileType: string;
  fileSizeBytes: number;
  category: string;
  phase?: string;
  uploadedById?: string;
  uploadedBy?: string;
  currentVersion: number;
  storageKey: string;
  isArchived: boolean;
  createdAt?: string;
  /** DRAFT → APPROVED (Review gate passed; version pinned) → STALE (re-uploaded after approval). */
  reviewStatus?: 'DRAFT' | 'APPROVED' | 'STALE';
  approvedVersion?: number;
}

export const documentApi = {
  getByOpportunityId: async (opportunityId: string): Promise<DocumentDto[]> => {
    const response = await axiosClient.get(`/documents/opportunity/${opportunityId}`);
    return response.data;
  },

  getByPhase: async (opportunityId: string, phase: string): Promise<DocumentDto[]> => {
    const response = await axiosClient.get(`/documents/opportunity/${opportunityId}/phase/${phase}`);
    return response.data;
  },

  getByFolder: async (folderId: string): Promise<DocumentDto[]> => {
    const response = await axiosClient.get(`/documents/folder/${folderId}`);
    return response.data;
  },

  uploadFile: async (
    file: File,
    opportunityId: string,
    category: string,
    phase?: string,
  ): Promise<DocumentDto> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('opportunityId', opportunityId);
    formData.append('category', category);
    if (phase) {
      formData.append('phase', phase);
    }
    const response = await axiosClient.post('/documents/upload-file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getDownloadUrl: async (documentId: string): Promise<string> => {
    const response = await axiosClient.get(`/documents/${documentId}/download`);
    return response.data.downloadUrl;
  },

  /**
   * Downloads a file via the stream endpoint with auth headers.
   * Fetches the file as a Blob and triggers a browser save-as dialog.
   */
  downloadFile: async (documentId: string, fileName: string): Promise<void> => {
    const response = await axiosClient.get(`/documents/${documentId}/stream`, {
      params: { download: true },
      responseType: 'blob',
    });
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  /**
   * Fetches the file as a Blob (with auth headers) and returns a temporary
   * object URL for inline preview. Caller must revoke it with URL.revokeObjectURL().
   */
  getPreviewBlobUrl: async (documentId: string): Promise<string> => {
    const response = await axiosClient.get(`/documents/${documentId}/stream`, {
      responseType: 'blob',
    });
    return URL.createObjectURL(response.data);
  },

  deleteDocument: async (documentId: string): Promise<void> => {
    await axiosClient.delete(`/documents/${documentId}`);
  },

  upload: async (dto: {
    opportunityId: string;
    fileName: string;
    fileType: string;
    fileSizeBytes: number;
    category: string;
    phase?: string;
  }, uploadedById: string): Promise<DocumentDto> => {
    const response = await axiosClient.post('/documents/upload', dto, {
      params: { uploadedById },
    });
    return response.data;
  },
};
